import * as Sentry from '@sentry/react';
import { sanitizeUrl } from './sanitizeUrl';

const PHI_KEYS = [
    'email',
    'name',
    'firstName',
    'lastName',
    'phone',
    'dob',
    'dateOfBirth',
    'address'
] as const;

function redactPiiKeys(obj: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => {
            const lowerKey = key.toLowerCase();
            const isPii = (PHI_KEYS as readonly string[]).some((k) => lowerKey === k.toLowerCase());
            return [key, isPii ? '[redacted]' : value];
        })
    );
}

const THIRD_PARTY_NOISE_PATTERNS = [
    /ERR_BLOCKED_BY_CLIENT/i,
    /youtubei\/v1\/log_event/i,
    /generate_204/i,
    /youtube\.com/i
] as const;

function isThirdPartyNoise(event: Sentry.ErrorEvent): boolean {
    const values = event.exception?.values ?? [];
    return values.some((ex) => {
        const message = ex.value ?? '';
        return THIRD_PARTY_NOISE_PATTERNS.some((pattern) => pattern.test(message));
    });
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
    const scrubbedUser = event.user?.id ? { id: event.user.id } : undefined;

    const scrubbedRequest = event.request
        ? {
              url: sanitizeUrl(event.request.url),
              method: event.request.method
          }
        : undefined;

    return {
        ...event,
        user: scrubbedUser,
        request: scrubbedRequest
    };
}

function resolveEnvironment(): string {
    if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
        return import.meta.env.VITE_SENTRY_ENVIRONMENT;
    }
    if (import.meta.env.VITE_STAGING === 'true') {
        return 'staging';
    }
    if (import.meta.env.MODE === 'production') {
        return 'production';
    }
    return 'development';
}

interface SentryConfig {
    dsn: string | undefined;
    release: string;
    enrichEvent?: (event: Sentry.ErrorEvent) => Sentry.ErrorEvent;
}

function initSentry({ dsn, release, enrichEvent }: SentryConfig): void {
    if (!dsn) return;

    Sentry.init({
        dsn,
        environment: resolveEnvironment(),
        release,
        ignoreErrors: ['ERR_BLOCKED_BY_CLIENT', /youtubei\/v1\/log_event/, /generate_204/],
        denyUrls: [/youtube\.com/, /googlevideo\.com/, /ytimg\.com/],
        tracesSampleRate: 0,
        normalizeDepth: 10,
        sendDefaultPii: false,
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: { request: false, response: false },
            httpBodies: [],
            queryParams: false
        },
        beforeSend(event) {
            // Enrich before the noise filter so it runs for dropped events too; scrub last so
            // the added context goes through the scrub as well.
            const enriched = enrichEvent ? enrichEvent(event) : event;
            if (isThirdPartyNoise(enriched)) return null;
            return scrubEvent(enriched);
        },
        beforeBreadcrumb(breadcrumb) {
            const category = breadcrumb.category ?? '';

            if (category === 'console') return null;

            if (['navigation', 'xhr', 'fetch'].includes(category)) {
                return {
                    ...breadcrumb,
                    data: breadcrumb.data
                        ? {
                              ...redactPiiKeys(breadcrumb.data as Record<string, unknown>),
                              url: sanitizeUrl(
                                  (breadcrumb.data as Record<string, unknown>)['url'] as
                                      string | undefined
                              ),
                              from: sanitizeUrl(
                                  (breadcrumb.data as Record<string, unknown>)['from'] as
                                      string | undefined
                              ),
                              to: sanitizeUrl(
                                  (breadcrumb.data as Record<string, unknown>)['to'] as
                                      string | undefined
                              )
                          }
                        : breadcrumb.data
                };
            }

            return breadcrumb;
        }
    });
}

interface UserWithDatabaseId {
    database: { data: { id: string } };
}

function setSentryUser(user: UserWithDatabaseId): void {
    Sentry.setUser({ id: user.database.data.id });
}

function clearSentryUser(): void {
    Sentry.setUser(null);
}

export { initSentry, setSentryUser, clearSentryUser };
